/**
 * @file qtyWithTonnes.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-20
 * @modified 2026-10-20
 * @description A line's quantity in the unit it is counted in AND the tonnes
 *              it comes to — one rule for orders, POs, invoices, credit notes,
 *              till (HW) invoices and bills, on screens and documents alike.
 *
 *   Cam, 20 Oct 2026: "PO, SO, INV, HW, and bills should in modals and tables
 *   and documents, show qty and supplied qty (if applicable) in the units
 *   chosen by the user AND in tonnes e.g. 2 in 0.5T Totes (1T) or 40 x 25kg
 *   Bags (1 Pallet) (1T) … It is otherwise very unclear at times whether
 *   things are matching up!"
 *
 *   WHAT IT SAYS (main / sub, and `text` for one line of plain text):
 *     packs    "40 × 25kg bag"        "1 pallet · 1.00 T"
 *              "2 × 500kg tote"       "1.00 T"
 *     tonnes   "12.50 T"              ''   — a 1000 kg unit, a bill line in
 *                                            tonnes, or no weight on a bulk
 *                                            sales account (201/202/204/206/
 *                                            209 — utils/tonnage's rule 2)
 *     kg       "2,000.00 kg"          "2.00 T"   — a bill line billed per kg
 *     none     "1"                    ''   — a line set to None, no weight
 *     unknown  "6.00"                 ''   — no weight anywhere: NEVER guessed
 *
 *   The pack count is a count (formatCount — "40", "1.5"); the tonnes follow
 *   the quantity rule with a capital T (formatTonnes — "1.00 T", "0.0375 T");
 *   kg keep the quantity rule. Pallets show only where the line has a pallet
 *   figure of its own, or a qty per pallet is known (the line's, else the
 *   item's) — never on a None, tonne or kg line. A negative quantity (a credit
 *   note's, a till refund's) keeps its sign on both figures.
 *
 *   `order: 'tonnes'` puts the tonnes first — a PO whose "Show quantities as"
 *   is Tonnes first (utils/poQtyDisplay): "1.00 T" over "40 × 25kg bag ·
 *   1 pallet". The PO PDF keeps its own two lines (poQtyDisplay.qtyDisplayFor).
 *
 *   Display only: nothing here changes a stored figure.
 *
 *   The server's utils/qtyWithTonnes.js is GENERATED from this file by
 *   scripts/sync-shared-utils.js — edit this one and re-run it. Its imports
 *   resolve to the server's own numberFormatters (generated), lineUnits
 *   (generated) and units.js (the same unitLabelFor / palletsFor rule).
 */

import { formatCount, formatQuantity, formatTonnes, roundQuantity } from './numberFormatters';
import { isNoWeight, lineWeightKg } from './lineUnits';
import { unitLabelFor, palletsFor } from './units';

/** Sales ledger codes whose quantity IS a tonnage with no weight set (utils/tonnage). */
export const BULK_TONNE_ACCOUNT_CODES = ['201', '202', '204', '206', '209'];

/** A sales account code that counts its quantity in tonnes (utils/tonnage rule 2). */
export function isBulkTonneAccount(code) {
  return BULK_TONNE_ACCOUNT_CODES.includes(String(code ?? '').trim().toUpperCase());
}

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const pos = (v) => {
  const n = num(v);
  return n !== null && n > 0 ? n : null;
};

/** "1 pallet", "2 pallets", "1.5 pallets" — '' for none. */
export function palletsText(pallets) {
  const n = num(pallets);
  if (n === null || n === 0) return '';
  return `${formatCount(n)} ${Math.abs(n) === 1 ? 'pallet' : 'pallets'}`;
}

/** A bill line's printed unit, read loosely: 'kg' | 't' | 'pack' | null. */
function unitOf(raw) {
  const s = String(raw ?? '').trim().toLowerCase();
  if (!s) return null;
  if (s === 'kg' || s === 'kgs' || s === 'kilo' || s === 'kilos' || s === 'kilogram' || s === 'kilograms') return 'kg';
  if (s === 't' || s === 'tonne' || s === 'tonnes' || s === 'ton' || s === 'tons' || s === 'mt') return 't';
  if (s === 'pack') return 'pack';
  return null;
}

const blankResult = (kind, main = '', title = '') => ({
  kind, quantity: null, main, sub: '', pallets: '', tonnes: null, tonnesText: '', text: main, title,
});

/**
 * @param {number|string|null} quantity
 * @param {object} [o]
 * @param {number|string|null} [o.unitWeightKg]      the line's own unit weight (0 = None, blank = the item's)
 * @param {number|string|null} [o.itemUnitWeightKg]  the item's unit weight
 * @param {number|string|null} [o.qtyPerPallet]      the line's own qty per pallet
 * @param {number|string|null} [o.itemQtyPerPallet]  the item's
 * @param {number|string|null} [o.pallets]           a pallet figure stored on the line (ordered quantity only)
 * @param {string|null}        [o.unit]              a bill line's unit: 'kg' | 't' | 'pack' (else the weight decides)
 * @param {string|null}        [o.accountCode]       a sales line's account — a bulk code with no weight counts in tonnes
 * @param {boolean}            [o.bulk]              the same, said directly
 * @param {string|null}        [o.kind]              'haulage' — a Direct Delivery haulage line (counts pallets)
 * @param {'units'|'tonnes'}   [o.order]             which comes first (default units)
 * @returns {{ kind: 'packs'|'tonnes'|'kg'|'none'|'unknown'|'pallets'|'blank',
 *   quantity: number|null, main: string, sub: string, pallets: string,
 *   tonnes: number|null, tonnesText: string, text: string, title: string }}
 */
export function quantityWithTonnes(quantity, o = {}) {
  const qty = num(quantity);
  if (qty === null) return blankResult('blank');

  // A Direct Delivery supplier haulage line's quantity is pallets.
  if (o.kind === 'haulage') {
    const main = palletsText(qty) || formatCount(qty);
    return { ...blankResult('pallets', main, 'Supplier haulage, charged per pallet — no tonnage'), quantity: qty };
  }

  const unit = unitOf(o.unit);
  const tonnesFirst = o.order === 'tonnes';
  const build = (kind, unitText, tonnes, extra = '', title = '') => {
    const tonnesText = tonnes === null ? '' : formatTonnes(tonnes);
    const parts = tonnesFirst ? [unitText, extra].filter(Boolean) : [extra, tonnesText].filter(Boolean);
    const main = tonnesFirst && tonnesText ? tonnesText : unitText;
    const sub = parts.join(' · ');
    const text = tonnesFirst && tonnesText
      ? `${tonnesText} (${[unitText, extra].filter(Boolean).join(' · ')})`
      : `${unitText}${extra ? ` (${extra})` : ''}${tonnesText ? ` · ${tonnesText}` : ''}`;
    return { kind, quantity: qty, main, sub, pallets: extra, tonnes, tonnesText, text, title };
  };

  // A bill line the invoice printed in kg or tonnes says its own unit.
  if (unit === 'kg') {
    return build('kg', `${formatQuantity(qty)} kg`, roundQuantity(qty / 1000), '', 'Billed per kg');
  }
  if (unit === 't') {
    const t = formatTonnes(qty);
    return { ...blankResult('tonnes', t, 'In tonnes'), quantity: qty, tonnes: roundQuantity(qty), tonnesText: t };
  }

  if (isNoWeight(o.unitWeightKg)) {
    const main = formatCount(qty);
    return { ...blankResult('none', main, 'None — no weight: a count, with no tonnage'), quantity: qty };
  }

  const kg = lineWeightKg(o.unitWeightKg, o.itemUnitWeightKg);
  if (kg === 1000) {
    const t = formatTonnes(qty);
    return { ...blankResult('tonnes', t, 'In tonnes'), quantity: qty, tonnes: roundQuantity(qty), tonnesText: t };
  }
  if (kg) {
    const label = unitLabelFor(kg);
    const perPallet = pos(o.qtyPerPallet) ?? pos(o.itemQtyPerPallet);
    const pallets = palletsFor(Math.abs(qty), perPallet, o.pallets);
    const signed = pallets === null ? null : (qty < 0 ? -pallets : pallets);
    const tonnes = roundQuantity((qty * kg) / 1000);
    return build('packs', `${formatCount(qty)} × ${label}`, tonnes, palletsText(signed),
      `${formatCount(qty)} × ${label} = ${formatTonnes(tonnes)}`);
  }

  if (o.bulk || isBulkTonneAccount(o.accountCode)) {
    const t = formatTonnes(qty);
    return {
      ...blankResult('tonnes', t, 'In tonnes — no unit weight on the item, and its sales account counts in tonnes'),
      quantity: qty, tonnes: roundQuantity(qty), tonnesText: t,
    };
  }

  const main = formatQuantity(qty);
  return { ...blankResult('unknown', main, 'No unit weight on this line or its item — its tonnes are not known'), quantity: qty };
}

/** Just the one line of text — "40 × 25kg bag (1 pallet) · 1.00 T". */
export function quantityWithTonnesText(quantity, o = {}) {
  return quantityWithTonnes(quantity, o).text;
}

/**
 * The facts a quantity needs, from a row in either naming: the server's
 * snake_case (unit_weight_kg, item_unit_weight_kg, qty_per_pallet,
 * item_qty_per_pallet, pallets, account_code, unit) or the camelCase of the
 * search endpoints and PO JSON (unitWeightKg, itemUnitWeightKg, qtyPerPallet,
 * itemQtyPerPallet, pallets, accountCode, kind).
 */
export function qtyFactsOf(line = {}) {
  const pick = (...keys) => {
    for (const k of keys) if (line?.[k] !== undefined && line?.[k] !== null && line?.[k] !== '') return line[k];
    return null;
  };
  return {
    unitWeightKg: pick('unit_weight_kg', 'unitWeightKg'),
    itemUnitWeightKg: pick('item_unit_weight_kg', 'itemUnitWeightKg'),
    qtyPerPallet: pick('qty_per_pallet', 'qtyPerPallet'),
    itemQtyPerPallet: pick('item_qty_per_pallet', 'itemQtyPerPallet'),
    pallets: pick('pallets'),
    accountCode: pick('account_code', 'accountCode'),
    unit: pick('qty_unit', 'qtyUnit'),
    kind: pick('kind'),
  };
}

/**
 * The tonnes a set of lines comes to, for a total row: [{ quantity, ...facts }]
 * (the same facts quantityWithTonnes takes). Lines of unknown weight are
 * counted apart, never as 0 or guessed: `unknown` says how many, `text` is
 * "24.00 T", and '' when no line's tonnes are known.
 */
export function tonnesTotal(rows = []) {
  let tonnes = 0;
  let known = 0;
  let unknown = 0;
  for (const r of rows || []) {
    const v = quantityWithTonnes(r?.quantity, r || {});
    if (v.kind === 'blank' || v.kind === 'pallets') continue;
    if (v.kind === 'none') { known += 1; continue; }
    if (v.tonnes === null) { unknown += 1; continue; }
    tonnes += v.tonnes;
    known += 1;
  }
  const t = roundQuantity(tonnes);
  const text = known && t ? formatTonnes(t) : '';
  const title = unknown ? `${unknown} line${unknown === 1 ? ' has' : 's have'} no unit weight, so ${unknown === 1 ? 'is' : 'are'} not in this total` : '';
  return { tonnes: t, known, unknown, text, title };
}
